import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { EnhancedButtonStatus } from '@components/button/EnhancedButton';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { drawerSections } from './content';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  checkSublineExists,
  createSubline,
  getSublineEnums,
  getSublineInfo,
  updateSubline,
} from './queries';
import { LookupToList, graphqlEntityToSublineInfo } from './utils';
import { cloneDeep } from 'lodash';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import { isEmpty } from '@utils/validationUtils';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { getError } from '@utils/graph-utils';
import { useNavigate } from 'react-router-dom';
import DynamicFormDrawer from '@components/dynamic-form/DynamicFormDrawer';
import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import { ISelectFormFieldProps } from '@components/dynamic-form/types/inputTypes';
import { normalizeDynamicFormDrawerValues } from '@components/dynamic-form/utils';

const SublineDrawer: React.FC<ISublineDrawerProps> = ({
  sublineId,
  lineId,
  lineName,
  isLineNameDisabled = true,
  canNavigateToDetailsPage = false,
  open,
  onSuccess,
  onClose,
}) => {
  const navigate = useNavigate();

  const [booted, setBooted] = useState<boolean>(false);
  const [formDisabled, setFormDisabled] = useState(false);
  const [submitButtonState, setSubmitButtonState] =
    useState<EnhancedButtonStatus>();

  const [inputsForm, setInputsForm] =
    useState<IDynamicFormWidgetSection[]>(drawerSections);

  const [sublineEnumQuery] = useLazyQuery(getSublineEnums());

  const [sublineInfoQuery, { loading }] = useLazyQuery(getSublineInfo(), {
    errorPolicy: 'all',
  });

  const [sublineAction] = useMutation(
    sublineId ? updateSubline() : createSubline()
  );

  const [checkSublineExistsLazy] = useLazyQuery(checkSublineExists(), {});

  const loadLovList = async () => {
    const result = await sublineEnumQuery();
    const sublineEnums = LookupToList(result.data);

    return sublineEnums;
  };

  const getEntityInfo = async () => {
    if (sublineId) {
      const apiResult = await sublineInfoQuery({
        variables: { id: sublineId },
      });
      if (apiResult.data) {
        const sublineEntity = graphqlEntityToSublineInfo(
          apiResult.data,
          lineId,
          lineName
        );
        return sublineEntity;
      }
    }

    return null;
  };

  const initialize = async () => {
    try {
      const updatedSections = cloneDeep(inputsForm);
      const updatedInputs = updatedSections[0].inputs;

      const [sublineEntity, lovData] = await Promise.all([
        getEntityInfo(),
        loadLovList(),
      ]);

      if (sublineId) {
        if (sublineEntity) {
          updatedInputs.lineName.value = sublineEntity.lineId;
          updatedInputs.sublineName.value = sublineEntity.sublineName;
          updatedInputs.sublineExternalCode.value =
            sublineEntity.sublineExternalCode;
          updatedInputs.arabicName.value = sublineEntity.arabicName;
          updatedInputs.sublineCategory.value = sublineEntity.sublineCategory;
          updatedInputs.sublineStatus.value = sublineEntity.sublineStatus;
        }
      }

      (updatedInputs.lineName as ISelectFormFieldProps).selectOptions =
        lovData['lines'];
      (updatedInputs.sublineCategory as ISelectFormFieldProps).selectOptions =
        lovData['PlanConfigManagement_SublineCategoryList'];

      updatedInputs.lineName.value = lineId;
      updatedInputs.lineName.disabled = isLineNameDisabled;

      setInputsForm(updatedSections);
      setBooted(true);
    } catch (err) {
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
  };

  const submitForm = async (values: Record<string, any>) => {
    const [data] = normalizeDynamicFormDrawerValues(drawerSections, values);
    setFormDisabled(true);
    setSubmitButtonState('loading');

    try {
      const variables = {
        subLineInputs: {
          externalCode: data.sublineExternalCode,
          lineID: lineId || data.lineName,
          name: data.sublineName,
          nameArabic: data.arabicName,
          category: data.sublineCategory,
          sublineStatus: data.sublineStatus,
        },
      };

      const res = await sublineAction({
        variables: sublineId
          ? { ...variables, entityId: sublineId }
          : variables,
        errorPolicy: 'all',
      });

      if (isEmpty(res.errors)) {
        toast.success(
          <ToastSuccessMessage>
            {sublineId
              ? 'Subline successfully updated'
              : 'Subline successfully created'}
          </ToastSuccessMessage>
        );

        if (isEmpty(lineId)) {
          const newSublineId =
            res.data.planConfigManagement.actions.createSubline.id;
          setTimeout(() => {
            setSubmitButtonState('success');
            setFormDisabled(false);
            onSuccess();
            onClose();
            navigate(`/plan/sublines/` + newSublineId);
          }, 500);
        } else {
          setTimeout(() => {
            setSubmitButtonState('success');
            setFormDisabled(false);
            onSuccess();
            onClose();
          }, 500);
        }
      } else {
        setFormDisabled(false);
        setSubmitButtonState(undefined);
        toast.error(<ToastErrorMessage>{getError(res)}</ToastErrorMessage>);
      }
    } catch (error) {
      setFormDisabled(false);
      setSubmitButtonState(undefined);
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    } finally {
      //
    }
  };

  const validateSublineExistsOnNameChange = async (
    selectedLineId?: string,
    sublineName?: string
  ): Promise<string> => {
    if (!isEmpty(lineId || selectedLineId) && !isEmpty(sublineName)) {
      const validationResponse = await checkSublineExistsLazy({
        variables: {
          lineId: lineId || selectedLineId,
          name: sublineName,
          sublineId: sublineId,
          externalCode: '',
        },
      });
      if (
        validationResponse.data.PlanConfigManagement?.queries
          ?.checkSublineExists.length > 0
      ) {
        return 'Subline Name already exists under the same Line';
      }
    }
    return '';
  };

  const validateSublineExistsOnExternalCodeChange = async (
    selectedLineId?: string,
    sublineExternalCode?: string
  ): Promise<string> => {
    if (!isEmpty(lineId || selectedLineId) && !isEmpty(sublineExternalCode)) {
      const validationResponse = await checkSublineExistsLazy({
        variables: {
          lineId: lineId || selectedLineId,
          externalCode: sublineExternalCode,
          sublineId: sublineId,
          name: '',
        },
      });
      if (
        validationResponse.data.PlanConfigManagement?.queries
          ?.checkSublineExists.length > 0
      ) {
        return 'Subline Name already exists under the same Line';
      }
    }
    return '';
  };

  const onCustomValidate = async (
    fieldName: string,
    values: Record<string, any>
  ) => {
    if (fieldName === 'sublineName') {
      return await validateSublineExistsOnNameChange(
        values.lineName,
        values[fieldName]
      );
    }

    if (fieldName === 'sublineExternalCode') {
      return await validateSublineExistsOnExternalCodeChange(
        values.lineName,
        values[fieldName]
      );
    }

    return '';
  };

  useEffect(() => {
    initialize();
  }, []);

  const onChange = async (
    fieldName: string,
    value: unknown,
    sectionIndex: number,
    values: Record<string, any>,
    errors: Record<string, string>,
    touched: Record<string, boolean>
  ) => {
    if (fieldName === 'lineName') {
      const [policyCoverError, sublineExternalCodeError] = await Promise.all([
        onCustomValidate('sublineName', values),
        onCustomValidate('sublineExternalCode', values),
      ]);
      errors.sublineName = policyCoverError;
      errors.sublineExternalCode = sublineExternalCodeError;

      touched.sublineName = true;
      touched.sublineExternalCode = true;
    }
  };

  const onCustomBlur = async (
    fieldName: string,
    values: Record<string, any>,
    errors: Record<string, string>
  ) => {
    if (fieldName === 'sublineName' || fieldName === 'sublineExternalCode') {
      if (!errors[fieldName]) {
        errors[fieldName] = await onCustomValidate(fieldName, values);
      }
    }

    if (fieldName === 'lineName') {
      if (values.sublineName) {
        errors.sublineName = await onCustomValidate('sublineName', values);
      }

      if (values.sublineExternalCode) {
        errors.sublineExternalCode = await onCustomValidate(
          'sublineExternalCode',
          values
        );
      }
    }
    return { values, errors };
  };

  const onCustomValidateForm = async (
    values: Record<string, any>,
    errors: Record<string, string>
  ) => {
    if (!errors.sublineName) {
      errors.sublineName = await onCustomValidate('sublineName', values);
    }

    if (!errors.sublineExternalCode) {
      errors.sublineExternalCode = await onCustomValidate(
        'sublineExternalCode',
        values
      );
    }

    return errors;
  };

  return (
    <DynamicFormDrawer
      title={sublineId ? 'Edit Subline' : 'New Subline'}
      loading={loading || !booted}
      onClose={() => onClose()}
      isOpen={open}
      sections={inputsForm}
      onSubmit={(values) => submitForm(values)}
      onChange={onChange}
      onCustomBlur={(f, v, e) => onCustomBlur(f, v, e)}
      onCustomValidate={onCustomValidateForm}
      submitButtonState={submitButtonState}
      disableForm={formDisabled}
    />
  );
};

export default SublineDrawer;
